import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button } from 'reactstrap';
import { getDocumotoPageDetails } from '../../api/documoto';
import type { DocumotoBomPartItemDto, DocumotoHotpointDto } from '../../api/documoto/types';
import { BomAddToCartCell, type BomCartAddPayload } from '../../components/Documoto/BomAddToCartCell';
import { bomPartFragmentId, escapePartIdForSelector } from '../../components/Documoto/documoto-bom-helpers';
import { DocumotoPageDiagramPanel, type HotspotActivatePayload } from '../../components/Documoto/DocumotoPageDiagramPanel';
import { DocumotoHoverPopoutImage } from '../../components/Documoto/DocumotoHoverPopoutImage';
import '../../components/Documoto/documoto-reference.scss';

type PageDetailLocationState = {
	pageTitle?: string;
	returnSerial?: string;
};

type CartLine = {
	partId: string;
	qty: number;
	partNumber?: string;
	partName?: string;
};

function displayPartSummary(row: DocumotoBomPartItemDto): { number?: string; name?: string } {
	return {
		number: row.partNumber ?? undefined,
		name: row.description ?? undefined,
	};
}

function partRowFromHighlight(
	highlight: HotspotActivatePayload,
	rows: DocumotoBomPartItemDto[],
	itemNumberToPartId: ReadonlyMap<string, string>,
): DocumotoBomPartItemDto | null {
	if (highlight.partId != null) {
		const pid = String(highlight.partId);
		const byPart = rows.find((r) => String(r.entityId) === pid);
		if (byPart) {
			return byPart;
		}
	}
	if (highlight.itemNumber != null) {
		const key = String(highlight.itemNumber).trim();
		if (key) {
			const mapped = itemNumberToPartId.get(key);
			if (mapped) {
				const r = rows.find((row) => String(row.entityId) === mapped);
				if (r) {
					return r;
				}
			}
			const r = rows.find((row) => {
				const it = row.lineItemNumber?.trim();
				return it != null && it === key;
			});
			if (r) {
				return r;
			}
		}
	}
	return null;
}

function collectPageHotpoints(items: DocumotoBomPartItemDto[], pageHotpoints: DocumotoHotpointDto[] | null | undefined): DocumotoHotpointDto[] {
	if (pageHotpoints && pageHotpoints.length > 0) {
		return pageHotpoints;
	}
	const m = new Map<string, DocumotoHotpointDto>();
	for (const it of items) {
		for (const h of it.hotpoints ?? []) {
			if (!m.has(h.key)) {
				m.set(h.key, h);
			}
		}
	}
	return [...m.values()];
}

type SelectedPartFocusCardProps = {
	row: DocumotoBomPartItemDto;
	onDismiss: () => void;
	onAddToCart: (p: BomCartAddPayload) => void;
};

function SelectedPartFocusCard({ row, onDismiss, onAddToCart }: SelectedPartFocusCardProps) {
	const s = displayPartSummary(row);
	return (
		<div className="part-focus-card" role="region" aria-label="Selected part details">
			<div className="part-focus-card-toolbar">
				<BomAddToCartCell
					partId={row.entityId}
					bomQuantity={row.quantity}
					partNumber={s.number}
					partName={s.name}
					onAdd={onAddToCart}
				/>
				<button type="button" className="part-focus-card-dismiss" aria-label="Clear part selection" onClick={onDismiss}>
					×
				</button>
			</div>
			<div className="part-focus-card-layout">
				<div className="part-focus-card-media">
					{row.rawImage ? (
						<DocumotoHoverPopoutImage
							src={row.rawImage}
							alt={s.name ? `${s.name} thumbnail` : 'Part thumbnail'}
							className="part-focus-card-thumb"
							hoverPopout
						/>
					) : (
						<div className="part-focus-card-thumb part-focus-card-thumb--empty">No image</div>
					)}
				</div>
				<div className="part-focus-card-body">
					<h3 className="part-focus-card-title">{s.number ?? '—'}</h3>
					<p className="part-focus-card-name">{s.name ?? '—'}</p>
					<dl className="part-focus-card-stats">
						<div>
							<dt>Qty</dt>
							<dd>{row.quantity ?? '—'}</dd>
						</div>
						<div>
							<dt>Part #</dt>
							<dd>{s.number ?? '—'}</dd>
						</div>
					</dl>
				</div>
			</div>
		</div>
	);
}

const DocumotoPageDetailPage = () => {
	const { entityId: entityIdParam } = useParams<{ entityId: string }>();
	const [searchParams] = useSearchParams();
	const location = useLocation();
	const navState = (location.state ?? null) as PageDetailLocationState | null;

	const serialFromQuery = searchParams.get('serial')?.trim() ?? '';
	const returnSerial = (navState?.returnSerial?.trim() || serialFromQuery).trim();

	const [busy, setBusy] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [title, setTitle] = useState('');
	const [diagramUrl, setDiagramUrl] = useState<string | null>(null);
	const [pageHotpoints, setPageHotpoints] = useState<DocumotoHotpointDto[]>([]);
	const [items, setItems] = useState<DocumotoBomPartItemDto[]>([]);
	const [bomHighlight, setBomHighlight] = useState<HotspotActivatePayload | null>(null);
	const [cartLines, setCartLines] = useState<CartLine[]>([]);
	const bomTableScrollRef = useRef<HTMLDivElement>(null);

	const pageId = entityIdParam?.trim() ?? '';

	const itemNumberToPartId = useMemo(() => {
		const m = new Map<string, string>();
		for (const r of items) {
			const it = r.lineItemNumber?.trim();
			if (it) {
				m.set(it, String(r.entityId));
			}
		}
		return m;
	}, [items]);

	const diagramHotpoints = useMemo(() => collectPageHotpoints(items, pageHotpoints), [items, pageHotpoints]);

	const cartUnitTotal = useMemo(() => cartLines.reduce((sum, l) => sum + l.qty, 0), [cartLines]);

	const addToCart = useCallback((p: BomCartAddPayload) => {
		const id = String(p.partId);
		setCartLines((prev) => {
			const i = prev.findIndex((l) => l.partId === id);
			if (i >= 0) {
				const next = [...prev];
				next[i] = { ...next[i], qty: next[i].qty + p.qty };
				return next;
			}
			return [
				...prev,
				{
					partId: id,
					qty: p.qty,
					partNumber: p.partNumber,
					partName: p.partName,
				},
			];
		});
	}, []);

	const selectedPartRow = useMemo(
		() => (bomHighlight ? partRowFromHighlight(bomHighlight, items, itemNumberToPartId) : null),
		[bomHighlight, items, itemNumberToPartId],
	);

	const headerSerial = returnSerial;

	const pageHeadingText = useMemo(() => {
		const t = title.trim() || (pageId ? `Page ${pageId}` : 'Page');
		const serial = headerSerial.trim();
		if (!serial) {
			return t;
		}
		return `${serial} — ${t}`;
	}, [title, pageId, headerSerial]);

	useEffect(() => {
		const id = pageId;
		if (!id) {
			setBusy(false);
			setError('Missing page id.');
			return;
		}

		let cancelled = false;
		(async () => {
			setBusy(true);
			setError(null);
			setBomHighlight(null);
			setCartLines([]);
			try {
				const res = await getDocumotoPageDetails(id);
				if (cancelled) {
					return;
				}
				if (res.errorMessage) {
					setError(res.errorMessage);
					setItems([]);
					setTitle('');
					setDiagramUrl(null);
					setPageHotpoints([]);
					return;
				}
				const fromNav = navState?.pageTitle?.trim();
				setTitle(fromNav || res.name?.trim() || `Page ${id}`);
				setItems(res.items ?? []);
				setDiagramUrl(res.diagramImageDataUrl?.trim() || null);
				setPageHotpoints(res.pageHotpoints ?? []);
			} catch (e) {
				if (!cancelled) {
					setError(e instanceof Error ? e.message : String(e));
				}
			} finally {
				if (!cancelled) {
					setBusy(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [pageId, location.state]);

	const scrollBomTableToHighlight = useCallback(
		(p: HotspotActivatePayload) => {
			const findRowEl = (): HTMLElement | null => {
				if (p.partId != null) {
					const byId = document.getElementById(bomPartFragmentId(p.partId));
					if (byId instanceof HTMLElement) {
						return byId;
					}
				}
				if (p.itemNumber != null) {
					const key = String(p.itemNumber).trim();
					if (!key) {
						return null;
					}
					const mapped = itemNumberToPartId.get(key);
					if (mapped) {
						const byPart = document.getElementById(bomPartFragmentId(mapped));
						if (byPart instanceof HTMLElement) {
							return byPart;
						}
					}
					const byAttr = document.querySelector(`[data-bom-item="${escapePartIdForSelector(key)}"]`);
					if (byAttr instanceof HTMLElement) {
						return byAttr;
					}
				}
				return null;
			};

			const scrollRowIntoBom = () => {
				const row = findRowEl();
				if (!row) {
					return;
				}
				const wrap = bomTableScrollRef.current;
				if (wrap && wrap.contains(row)) {
					const wrapRect = wrap.getBoundingClientRect();
					const rowRect = row.getBoundingClientRect();
					const rowCenter = rowRect.top + rowRect.height / 2;
					const wrapCenter = wrapRect.top + wrapRect.height / 2;
					const delta = rowCenter - wrapCenter;
					wrap.scrollTo({
						top: wrap.scrollTop + delta,
						behavior: 'smooth',
					});
					return;
				}
				row.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'nearest',
				});
			};

			requestAnimationFrame(() => {
				requestAnimationFrame(scrollRowIntoBom);
			});
		},
		[itemNumberToPartId],
	);

	const onHotspotActivate = useCallback(
		(p: HotspotActivatePayload) => {
			setBomHighlight(p);
			scrollBomTableToHighlight(p);
		},
		[scrollBomTableToHighlight],
	);

	const onBomTableRowActivate = useCallback((row: DocumotoBomPartItemDto) => {
		const itemNum = row.lineItemNumber?.trim();
		setBomHighlight({
			partId: row.entityId,
			itemNumber: itemNum ?? undefined,
		});
	}, []);

	const backPartsSearchState =
		returnSerial.trim().length > 0 ? { serial: returnSerial.trim(), autoSearch: true as const } : undefined;

	if (busy) {
		return (
			<div className="documoto-ref documoto-ref--page-detail py-3 px-2">
				<p className="toc-muted">Loading…</p>
			</div>
		);
	}

	return (
		<div className="documoto-ref documoto-ref--page-detail py-3 px-2">
			<header className="page-detail-header">
				<Button tag={Link} to="/admin/documoto-parts" state={backPartsSearchState} color="light" className="mb-2">
					{returnSerial.trim()
						? `← Back to parts manual search (${returnSerial.trim()})`
						: '← Back to parts manual search'}
				</Button>
				<h1 className="page-detail-title">{pageHeadingText}</h1>
			</header>

			{error ? (
				<Alert color="danger" role="alert" className="mt-2">
					{error}
				</Alert>
			) : null}

			{!error ? (
				<div className="page-detail-grid">
					<section
						className={
							'page-detail-diagram panel' + (selectedPartRow ? ' page-detail-diagram--has-selection' : '')
						}
					>
						<div className="diagram-panel-host">
							<DocumotoPageDiagramPanel
								key={diagramUrl ?? pageId}
								diagramImageDataUrl={diagramUrl}
								hotpoints={diagramHotpoints}
								itemNumberToPartId={itemNumberToPartId}
								onHotspotActivate={onHotspotActivate}
							/>
						</div>
						{selectedPartRow ? (
							<SelectedPartFocusCard
								row={selectedPartRow}
								onDismiss={() => setBomHighlight(null)}
								onAddToCart={addToCart}
							/>
						) : null}
					</section>

					<aside className="page-detail-side">
						<section className="panel bom-panel">
							<div className="bom-panel-heading">
								<h2>Bill of materials</h2>
								{cartUnitTotal > 0 ? (
									<span className="bom-cart-summary" aria-live="polite">
										Cart: {cartUnitTotal} {cartUnitTotal === 1 ? 'unit' : 'units'}
									</span>
								) : null}
							</div>
							{items.length === 0 ? <p className="toc-muted">No BOM lines.</p> : null}
							{items.length > 0 ? (
								<div ref={bomTableScrollRef} className="bom-table-wrap" id="bom-table-scroll">
									<table className="bom-table">
										<thead>
											<tr>
												<th className="bom-th-num" scope="col">
													#
												</th>
												<th className="bom-th-thumb" scope="col">
													{' '}
												</th>
												<th scope="col">Part #</th>
												<th scope="col">Qty</th>
												<th scope="col">Description</th>
												<th className="bom-th-cart" scope="col">
													<span className="sr-only">Add to cart</span>
												</th>
											</tr>
										</thead>
										<tbody>
											{items.map((row) => {
												const s = displayPartSummary(row);
												const itemNum = row.lineItemNumber?.trim();
												const isHighlighted =
													bomHighlight &&
													((bomHighlight.partId != null && String(bomHighlight.partId) === String(row.entityId)) ||
														(bomHighlight.itemNumber != null &&
															itemNum != null &&
															String(bomHighlight.itemNumber).trim() === itemNum));
												return (
													<tr
														key={row.entityId}
														id={bomPartFragmentId(row.entityId)}
														data-part-id={String(row.entityId)}
														{...(itemNum != null ? { 'data-bom-item': itemNum } : {})}
														className={'bom-row' + (isHighlighted ? ' bom-row--highlighted' : '')}
														onClick={(e) => {
															if ((e.target as HTMLElement).closest('.bom-cart-cell')) {
																return;
															}
															onBomTableRowActivate(row);
														}}
													>
														<td className="bom-td-num">{itemNum != null ? itemNum : ''}</td>
														<td className="bom-td-thumb">
															{row.rawImage ? (
																<DocumotoHoverPopoutImage
																	src={row.rawImage}
																	alt=""
																	className="part-table-thumb"
																	hoverPopout
																	hoverPopoutPlacement="left"
																/>
															) : (
																<span className="toc-muted small">—</span>
															)}
														</td>
														<td className="bom-td-partnum">{s.number ?? '—'}</td>
														<td>{row.quantity ?? '—'}</td>
														<td className="bom-desc">{s.name ?? '—'}</td>
														<td className="bom-cart-col">
															<BomAddToCartCell
																partId={row.entityId}
																bomQuantity={row.quantity}
																partNumber={s.number}
																partName={s.name}
																onAdd={addToCart}
															/>
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							) : null}
						</section>
					</aside>
				</div>
			) : null}
		</div>
	);
};

export default DocumotoPageDetailPage;
